import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}

/**
 * Editable / view block for the main column of the ChallengeDetail dialog.
 * Renders inputs in `editing` mode, otherwise read-only views.
 *
 * NOTE on XSS: descriptionHtml is also sanitized at the parent (ChallengeDetail)
 * boundary. We sanitize again here so this component is safe regardless of how
 * the parent wires `safeDescriptionHtml` (defense in depth — caller may forget).
 */
export default function ChallengeDetailFields({ challenge, editing, form, setForm, safeDescriptionHtml }) {
    const change = (key) => (e) => setForm({ ...form, [key]: e.target.value });
    const innerSafeHtml = useMemo(
        () =>
            DOMPurify.sanitize(safeDescriptionHtml || "", {
                ALLOWED_TAGS: ["p", "strong", "em", "ul", "ol", "li", "br", "b", "i", "span"],
                ALLOWED_ATTR: [],
            }),
        [safeDescriptionHtml],
    );
    return (
        <>
            <Field label="Title">
                {editing ? (
                    <Input
                        className="bg-black border-white/10 rounded-none font-display font-bold text-lg"
                        value={form.title}
                        onChange={change("title")}
                        data-testid="edit-title"
                    />
                ) : null}
            </Field>

            <Field label="Short description">
                {editing ? (
                    <Textarea
                        className="bg-black border-white/10 rounded-none"
                        value={form.shortDescription}
                        onChange={change("shortDescription")}
                        data-testid="edit-short-desc"
                    />
                ) : (
                    <p className="text-white/80">{challenge.shortDescription}</p>
                )}
            </Field>

            <Field label="Description (HTML)">
                {editing ? (
                    <Textarea
                        rows={10}
                        className="bg-black border-white/10 rounded-none font-mono text-xs"
                        value={form.descriptionHtml}
                        onChange={change("descriptionHtml")}
                        data-testid="edit-description-html"
                    />
                ) : (
                    <div
                        className="challenge-html text-white/80"
                        dangerouslySetInnerHTML={{ __html: innerSafeHtml }}
                    />
                )}
            </Field>

            <Field label="Rules">
                {editing ? (
                    <Textarea
                        rows={5}
                        className="bg-black border-white/10 rounded-none font-mono text-xs"
                        value={form.rules}
                        onChange={change("rules")}
                        data-testid="edit-rules"
                    />
                ) : (
                    <ul className="space-y-1 text-sm text-white/80 list-square pl-4">
                        {challenge.rules?.map((r) => (
                            <li key={r} className="list-disc">
                                {r}
                            </li>
                        ))}
                    </ul>
                )}
            </Field>

            <Field label="Scoring">
                {editing ? (
                    <Textarea
                        rows={4}
                        className="bg-black border-white/10 rounded-none font-mono text-xs"
                        value={form.scoring}
                        onChange={change("scoring")}
                        data-testid="edit-scoring"
                    />
                ) : (
                    <ul className="space-y-1 text-sm text-white/80 pl-4">
                        {challenge.scoring?.map((r) => (
                            <li key={r} className="list-disc">
                                {r}
                            </li>
                        ))}
                    </ul>
                )}
            </Field>

            <Field label="Hint">
                {editing ? (
                    <Input
                        className="bg-black border-white/10 rounded-none"
                        value={form.hint}
                        onChange={change("hint")}
                        data-testid="edit-hint"
                    />
                ) : (
                    <p className="text-white/80 italic">{challenge.hint}</p>
                )}
            </Field>

            <Field label="Email promo line">
                {editing ? (
                    <Input
                        className="bg-black border-white/10 rounded-none"
                        value={form.emailPromo}
                        onChange={change("emailPromo")}
                        data-testid="edit-email-promo"
                    />
                ) : (
                    <p className="text-white/80">{challenge.emailPromo}</p>
                )}
            </Field>

            <Field label="Story context">
                {editing ? (
                    <Textarea
                        className="bg-black border-white/10 rounded-none"
                        value={form.storyContext}
                        onChange={change("storyContext")}
                        data-testid="edit-story-context"
                    />
                ) : (
                    <p className="text-white/80">{challenge.storyContext}</p>
                )}
            </Field>
        </>
    );
}
